import { GetWorkOrdersRequestDTO, RecordState, WorkOrderStatus } from '@gearbelt/global';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';

export const workOrderKeys = {
  all: ['work-orders'] as const,
  detail: (workOrderId: string | undefined) => [...workOrderKeys.all, 'detail', workOrderId] as const,
  actions: (workOrderId: string | undefined, isAWA: boolean) => [...workOrderKeys.all, 'actions', workOrderId, isAWA] as const,
  infinite: (
    search: string,
    filters?: GetWorkOrdersRequestDTO['filters'],
    state?: RecordState,
    groupBy?: GetWorkOrdersRequestDTO['groupBy'],
    orderBy?: GetWorkOrdersRequestDTO['orderBy'],
  ) => [...workOrderKeys.all, 'infinite', search, filters, state, groupBy, orderBy] as const,
};

export const awaWorkOrderKeys = {
  all: ['awa-work-orders'] as const,
  forUser: (user: unknown) => [...awaWorkOrderKeys.all, user] as const,
};

export const getWorkOrdersInfiniteQueryOptions = ({
  search,
  filters,
  state,
  groupBy,
  orderBy,
  enabled = true,
}: {
  search: string;
  filters?: GetWorkOrdersRequestDTO['filters'];
  state?: RecordState;
  groupBy?: GetWorkOrdersRequestDTO['groupBy'];
  orderBy?: GetWorkOrdersRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: workOrderKeys.infinite(search, filters, state, groupBy, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (
        await ApiService.GetWorkOrders({
          limit: 30,
          cursors: pageParam,
          filters: { search, state, ...filters },
          groupBy,
          orderBy,
        })
      ).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    enabled,
  });
};

export const getWorkOrderByIdQueryOptions = ({ workOrderId }: { workOrderId: string | undefined }) => {
  return queryOptions({
    queryKey: workOrderKeys.detail(workOrderId),
    queryFn: async () => {
      if (!workOrderId) throw new Error('No work order ID');
      return (await (await ApiService.GetWorkOrderById({ workOrderId })).json()).workOrder;
    },
    enabled: !!workOrderId,
  });
};

export const getWorkOrderActionsInfiniteQueryOptions = ({ workOrderId, isAWA = false }: { workOrderId: string | undefined; isAWA?: boolean }) => {
  return infiniteQueryOptions({
    queryKey: workOrderKeys.actions(workOrderId, isAWA),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (
        await (isAWA ? AWAApiV2Service : ApiService).GetActions({
          filters: { fields: { workOrderId: [workOrderId ?? ''] } },
          orderBy: { field: 'createdAt', direction: 'desc' },
          limit: 30,
          cursors: pageParam,
        })
      ).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    enabled: !!workOrderId,
  });
};

export const getAWAWorkOrdersQueryOptions = ({ user }: { user: unknown }) => {
  return queryOptions({
    queryKey: awaWorkOrderKeys.forUser(user),
    queryFn: async () =>
      (
        await AWAApiV2Service.GetWorkOrders({
          filters: { fields: { status: [WorkOrderStatus.Open] }, assignedToCurrentUser: true },
        })
      ).json(),
  });
};
